import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import bankAccountService from '../../services/bankAccountService';

const OwnerBankInfo = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState({
        bank_name: '',
        account_number: '',
        account_type: 'Ahorros',
        account_holder: '',
        keywords: ''
    });

    const BANKS = [
        'Banco General',
        'Banistmo',
        'BAC Credomatic',
        'Global Bank',
        'Multibank',
        'St. Georges Bank',
        'Caja de Ahorros',
        'Banco Nacional',
        'Scotiabank',
        'Otro'
    ];

    useEffect(() => {
        fetchMyAccount();
    }, []);

    const fetchMyAccount = async () => {
        try {
            const response = await bankAccountService.getMyAccount();
            if (response.success && response.account) {
                setFormData({
                    bank_name: response.account.bank_name || '',
                    account_number: response.account.account_number || '',
                    account_type: response.account.account_type || 'Ahorros',
                    account_holder: response.account.account_holder || '',
                    keywords: response.account.keywords || ''
                });
            }
        } catch (error) {
            console.error('Error fetching bank info:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (message.text) setMessage({ type: '', text: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await bankAccountService.saveMyAccount(formData);
            if (response.success) {
                setMessage({ type: 'success', text: 'Información guardada correctamente' });
            } else {
                setMessage({ type: 'error', text: response.message || 'Error al guardar' });
            }
        } catch (error) {
            console.error('Error saving bank info:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Error del servidor'
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold', color: '#1a237e' }}>
                Mi Información Bancaria
            </Typography>

            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Esta información se utiliza para reembolsos y para identificar sus pagos automáticamente.
                </Alert>

                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 3 }}>
                        {message.text}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'grid', gap: 3 }}>

                        <FormControl fullWidth required>
                            <InputLabel>Banco</InputLabel>
                            <Select
                                name="bank_name"
                                value={formData.bank_name}
                                label="Banco"
                                onChange={handleChange}
                            >
                                {BANKS.map(bank => (
                                    <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Número de Cuenta"
                            name="account_number"
                            value={formData.account_number}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText="Sin guiones ni espacios"
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Tipo de Cuenta</InputLabel>
                            <Select
                                name="account_type"
                                value={formData.account_type}
                                label="Tipo de Cuenta"
                                onChange={handleChange}
                            >
                                <MenuItem value="Ahorros">Ahorros</MenuItem>
                                <MenuItem value="Corriente">Corriente</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Nombre del Titular"
                            name="account_holder"
                            value={formData.account_holder}
                            onChange={handleChange}
                            fullWidth
                            helperText="Tal como aparece en el banco"
                        />

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Palabras Clave (Para Conciliación Automática)
                                </Typography>
                                <Tooltip title="Ingrese nombres, apellidos o números que aparecen en la descripción de sus transferencias bancarias. Use comas para separar múltiples valores. Ejemplo: VILLAS 45, PEDRO PEREZ">
                                    <IconButton size="small" sx={{ ml: 1 }}>
                                        <InfoIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <TextField
                                name="keywords"
                                value={formData.keywords}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Ejemplo: CASA 45, ROLY GONZALEZ, PAGO MANTENIMIENTO"
                                helperText="El sistema buscará estas palabras en los estados de cuenta bancarios para identificar sus pagos automáticamente."
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={saving}
                                sx={{ minWidth: 150 }}
                            >
                                {saving ? <CircularProgress size={24} color="inherit" /> : 'Guardar Cambios'}
                            </Button>
                        </Box>

                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default OwnerBankInfo;
